// New Inquiry modal, two stages. Stage 1 "header": restaurant (API search) +
// payment option + notes → POST ums/v1/cms/restaurant-inquiries WITHOUT items
// (header-only inquiries are valid; status defaults to NEW server-side).
// Stage 2 "items": the same modal morphs into the item cards board for the
// created inquiry — each card persists individually (see item-board/), so
// stage 2 has no footer button: the wizard's "Save item" is the only action,
// and closing (the X) fires onCreated so the caller navigates to the detail
// screen; everything visible is already saved.

import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from '@elkaso-app/web-design';
import {
  CreateRestaurantInquiryPayload,
  PaymentOption,
  RestaurantOption,
  useCreateRestaurantInquiryApi,
  useSearchRestaurantsApi,
} from 'apis/restaurant-inquiries';
import { ItemCardsBoard } from 'pages/inquiry-processor/components/item-board';
import type { Inquiry } from 'pages/inquiry-processor/types';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { isCreditOption, paymentLabel, PAYMENT_OPTION_CHOICES } from 'pages/inquiry-processor/utils/payment';
import { InquiryStore } from 'pages/inquiry-processor/utils/storage';
import { useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const numOnly = (v: string) => v.replace(/[^0-9]/g, '');

export const NewInquiryModal = ({ isOpen, onClose, onCreated }: Props) => {
  const toast = useToast();
  const { loadOptions } = useSearchRestaurantsApi();
  const createInquiry = useCreateRestaurantInquiryApi();

  const [stage, setStage] = useState<'header' | 'items'>('header');
  const [createdId, setCreatedId] = useState<number | null>(null);
  const [restaurant, setRestaurant] = useState<RestaurantOption | null>(null);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(PaymentOption.CASH);
  const [creditDays, setCreditDays] = useState('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setStage('header');
    setCreatedId(null);
    setRestaurant(null);
    setPaymentOption(PaymentOption.CASH);
    setCreditDays('');
    setNotes('');
  };

  // In stage 2 the inquiry (and any completed cards) already exist — closing
  // by any path must behave like "Done" so the caller navigates to it.
  const close = () => {
    const id = createdId;
    const wasItems = stage === 'items';
    reset();
    onClose();
    if (wasItems && id) onCreated(String(id));
  };

  const createHeader = async () => {
    if (!restaurant) {
      toast({ title: 'Please select a restaurant', status: 'warning' });
      return;
    }
    const isCredit = isCreditOption(paymentOption);
    const days = isCredit ? Number(creditDays) || 0 : 0;
    // status omitted on purpose: backend defaults to NEW; items come in
    // stage 2 via POST :id/items, one per completed card.
    const payload: CreateRestaurantInquiryPayload = { restaurantId: restaurant.value, paymentOption };
    if (isCredit) payload.creditDays = days;
    if (notes.trim()) payload.notes = notes.trim();

    let created;
    try {
      created = await createInquiry.mutateAsync(payload);
    } catch (err) {
      toast({ title: 'Failed to create inquiry', description: apiErrorMessage(err), status: 'error' });
      return;
    }

    // Mirror the HEADER into the local store (keyed by the BACKEND id — the
    // detail screen routes and mirrors by it). Items sync via the detail
    // screen's existing API→store mirror as cards are saved.
    const inq: Inquiry = {
      id: String(created.id),
      backendId: created.id,
      company: restaurant.label,
      restaurantId: restaurant.value,
      credit: paymentLabel(paymentOption, days),
      paymentOption,
      creditDays: days,
      additionalReq: notes.trim(),
      status: 'new',
      items: [],
      subInquiries: [],
      createdAt: Date.now(),
    };
    InquiryStore.upsert(inq);
    toast({ title: 'Inquiry created — now add its items', status: 'success' });
    setCreatedId(created.id);
    setStage('items');
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size={{ base: 'full', md: '6xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent maxW={{ base: '100vw', md: stage === 'header' ? '720px' : 'min(1880px, 98vw)' }}>
        <ModalHeader>{stage === 'header' ? 'New Inquiry' : `Items — ${restaurant?.label ?? ''}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {stage === 'header' ? (
            <>
              <Grid templateColumns='1fr' gap='3' mb='2'>
                <FormControl isRequired>
                  <FormLabel fontSize='sm'>Company / Restaurant</FormLabel>
                  <ChakraAsyncSelect
                    loadOptions={loadOptions}
                    defaultOptions
                    value={restaurant}
                    onChange={(opt: any) => setRestaurant(opt || null)}
                    isClearable
                    placeholder='Search restaurants…'
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    styles={{ menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 2000 }) }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize='sm'>Payment Option</FormLabel>
                  <Select value={paymentOption} onChange={(e) => setPaymentOption(e.target.value as PaymentOption)}>
                    {PAYMENT_OPTION_CHOICES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {isCreditOption(paymentOption) && (
                  <FormControl isRequired>
                    <FormLabel fontSize='sm'>Credit Days</FormLabel>
                    <Input
                      value={creditDays}
                      onChange={(e) => setCreditDays(numOnly(e.target.value))}
                      placeholder='e.g. 30'
                      inputMode='numeric'
                    />
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize='sm'>Additional Requirements / Notes</FormLabel>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder='e.g. No Sadia brand, prefer Brazilian origin'
                  />
                </FormControl>
              </Grid>
            </>
          ) : (
            createdId && <ItemCardsBoard inquiryId={createdId} />
          )}
        </ModalBody>
        {stage === 'header' && (
          <ModalFooter gap='2'>
            <Button variant='outline' onClick={close} isDisabled={createInquiry.isLoading}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={createHeader} isLoading={createInquiry.isLoading} loadingText='Creating…'>
              Create & Add Items →
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
